import React, { useMemo, useEffect, useState } from 'react';
import SubTableV2 from "./SubTableV2";

function TransactionDetailsModal({ isOpen, details, onClose }) {
  const [formattedDetails, setFormattedDetails] = useState(null);

  useEffect(() => {
    if (details) {
      const lineItemsData = details.lineItems.map(item => {
        return {
          ...item,
          RetailPPU: new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 3,
            maximumFractionDigits: 3
          }).format(item.RetailPPU),
          UserPPU: new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 3,
            maximumFractionDigits: 3
          }).format(item.UserPPU),
          UserAmount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.UserAmount),
          UserTransactionFee: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.UserTransactionFee),
          Discount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((item.RetailPPU * item.Quantity - item.UserAmount) + item.UserTransactionFee),
          Amount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.Amount),
          PPU: new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 3,
            maximumFractionDigits: 3
          }).format(item.PPU),
          DiscAmount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.DiscAmount),
          // Use server-calculated profit and margin (calculated with raw PPU logic and CarrierFee allocation)
          Profit: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.LineItemProfit || 0),
          Margin: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.Margin || 0),
          // Display CarrierFee for debugging/transparency (only for LineNumber = 0)
          CarrierFeeApplied: item.LineNumber === 0 ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.CarrierFee || 0) : '$0.00'
        };
      });

      const taxesData = details.transLineTaxes.map(tax => ({
        ...tax,
        amount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(tax.amount)
      }));

      setFormattedDetails({ ...details, lineItems: lineItemsData, transLineTaxes: taxesData });
    }
  }, [details]);

  const lineItemsColumns = useMemo(() => [
    { name: 'Category', selector: row => row.Category, sortable: true, width: '200px' },
    { name: 'Retail Price', selector: row => row.RetailPPU, sortable: true },
    { name: 'Paid Price', selector: row => row.PPU, sortable: true },
    { name: 'User Price', selector: row => row.UserPPU, sortable: true },
    { name: 'Margin', selector: row => row.Margin, sortable: true },
    { name: 'Discount', selector: row => row.DiscAmount, sortable: true },
    { name: 'Quantity', selector: row => row.Quantity, sortable: true },
    { name: 'Paid Amount', selector: row => row.Amount, sortable: true },
    { name: 'User Amount', selector: row => row.UserAmount, sortable: true },
    { name: 'User Fee', selector: row => row.UserTransactionFee, sortable: true },
    { name: 'Carrier Fee', selector: row => row.CarrierFeeApplied, sortable: true },
    { name: 'Profit', selector: row => row.Profit, sortable: true,
      cell: row => (
        <span style={{ color: parseFloat(row.Profit.replace(/[$,]/g, '')) < 0 ? 'red' : 'green', fontWeight: 'bold' }}>
          {row.Profit}
        </span>
      )
    }
  ], []);

  const transLineTaxesColumns = useMemo(() => [
    { name: 'Tax Description', selector: row => row.taxDescription, sortable: true },
    { name: 'Amount', selector: row => row.amount, sortable: true }
  ], []);

  const transactionInfosColumns = useMemo(() => [
    { name: 'Type', selector: row => row.Type, sortable: true },
    { name: 'Value', selector: row => row.Value, sortable: true }
  ], []);

  if (!isOpen || !formattedDetails) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-50">
      {/* Modal Container */}
      <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 rounded-t-lg sm:rounded-t-xl">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">Admin Transaction Details</h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">ID: {details.transactions.TransactionId}</p>
            </div>
          </div>
          <button
            className="btn-base btn-ghost btn-xs sm:btn-sm flex-shrink-0"
            onClick={onClose}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          {/* Transaction Summary */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 border border-blue-200">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Transaction Summary</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
              {Object.entries(details.transactions).map(([key, value]) => {
                let formattedValue = value;
                if (key === "POSDate" || key === "TransactionDate") {
                  formattedValue = new Date(value).toLocaleDateString();
                } else if (typeof value === "boolean") {
                  formattedValue = value ? 'Yes' : 'No';
                } else if (["CarrierFee", "DiscAmount", "FundedTotal", "NetTotal", "PrefTotal", "SettleAmount"].includes(key)) {
                  formattedValue = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
                } else if (value === null) {
                  formattedValue = 'N/A';
                }

                return (
                  <div key={key} className="flex flex-col">
                    <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </span>
                    <span className="text-sm text-gray-900 font-medium">{formattedValue}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tables */}
          <div className="space-y-6">
            <SubTableV2
              title="Line Items"
              columns={lineItemsColumns}
              data={formattedDetails.lineItems}
            />
            <SubTableV2
              title="Taxes Paid (Included in the amount)"
              columns={transLineTaxesColumns}
              data={formattedDetails.transLineTaxes}
            />
            <SubTableV2
              title="Prompts / Information"
              columns={transactionInfosColumns}
              data={details.transactionInfos}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 sm:gap-3 p-3 sm:p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg sm:rounded-b-xl">
          <button
            className="btn-base btn-secondary btn-sm sm:btn-base"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default TransactionDetailsModal;




// import React, { useMemo, useEffect, useState } from 'react';
// import SubTable from './SubTable';

// function TransactionModalAdmin({ isOpen, details, onClose }) {
//   const [formattedDetails, setFormattedDetails] = useState(null);

//   useEffect(() => {
//     if (details) {
//       const lineItemsData = details.lineItems.map(item => {
//         // Calculate the profit and margin for each line item
//         const profit = item.UserAmount - item.Amount;
//         const margin = item.UserPPU - item.PPU;

//         return {
//           ...item,
//           RetailPPU: new Intl.NumberFormat('en-US', {
//             style: 'currency',
//             currency: 'USD',
//             minimumFractionDigits: 3,
//             maximumFractionDigits: 3
//           }).format(item.RetailPPU),
//           UserPPU: new Intl.NumberFormat('en-US', {
//             style: 'currency',
//             currency: 'USD',
//             minimumFractionDigits: 3,
//             maximumFractionDigits: 3
//           }).format(item.UserPPU),
//           UserAmount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.UserAmount),
//           UserTransactionFee: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.UserTransactionFee),
//           Discount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((item.RetailPPU * item.Quantity - item.UserAmount) + item.UserTransactionFee),
//           // Format the added fields as currency
//           Amount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.Amount),
//           PPU: new Intl.NumberFormat('en-US', {
//             style: 'currency',
//             currency: 'USD',
//             minimumFractionDigits: 3,
//             maximumFractionDigits: 3
//           }).format(item.PPU),
//           DiscAmount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.DiscAmount),
//           // Add the profit and margin, formatted as currency
//           Profit: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(profit),
//           Margin: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(margin)
//         };
//       });

//       const taxesData = details.transLineTaxes.map(tax => ({
//         ...tax,
//         amount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(tax.amount)
//       }));

//       setFormattedDetails({ ...details, lineItems: lineItemsData, transLineTaxes: taxesData });
//     }
//   }, [details]);

//   const transLineTaxesColumns = useMemo(() => [
//     { Header: 'Tax Description', accessor: 'taxDescription' },
//     { Header: 'Amount', accessor: 'amount' }
//   ], []);

//   const transactionInfosColumns = useMemo(() => [
//     { Header: 'Type', accessor: 'Type' },
//     { Header: 'Value', accessor: 'Value' }
//   ], []);

//   const lineItemsColumns = useMemo(() => [
//     { Header: 'Category', accessor: 'Category' },
//     { Header: 'Retail Price', accessor: 'RetailPPU' },
//     { Header: 'Paid Price', accessor: 'PPU' },
//     { Header: 'User Price', accessor: 'UserPPU' },
//     { Header: 'Margin', accessor: 'Margin' },
//     { Header: 'Discount', accessor: 'DiscAmount' },
//     { Header: 'Quantity', accessor: 'Quantity' },
//     { Header: 'Paid Amount', accessor: 'Amount' },
//     { Header: 'User Amount', accessor: 'UserAmount' },
//     { Header: 'User Fee', accessor: 'UserTransactionFee' },
//     { Header: 'Profit', accessor: 'Profit' },
//   ], []);

//   if (!isOpen || !formattedDetails) {
//     return null;
//   }

//   return (
//     <div className="transaction-modal">
//       <div className="modal-overlay absolute inset-0 bg-black opacity-50"></div>
//       <div className="transaction-modal-content">
//         <button className="absolute top-2 right-2 themed-button" onClick={onClose}>Close</button>
//         <h3 className="text-xl font-bold mb-4">Transaction Details</h3>

//         <div className="grid grid-cols-1 md:grid-cols-3 gap-1 mb-4">
//           {/* Iterate over the transaction details object and create a div for each key-value pair */}
//           {Object.entries(details.transactions).map(([key, value]) => {
//             // Format the date fields
//             if (key === "POSDate" || key === "TransactionDate") {
//               value = new Date(value).toLocaleDateString();
//             }
//             // Format the boolean fields
//             else if (typeof value === "boolean") {
//               value = value ? 'Yes' : 'No';
//             }
//             // Format the currency fields
//             else if (key === "CarrierFee" || key === "DiscAmount" || key === "FundedTotal" || key === "NetTotal" || key === "PrefTotal" || key === "SettleAmount") {
//               value = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
//             }
//             // Check for null values
//             else if (value === null) {
//               value = 'N/A'; // or some other placeholder text
//             }

//             return (
//               <div key={key} className="flex justify-start items-center">
//                 <strong className="mr-2">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong>
//                 <span>{value}</span>
//               </div>
//             );
//           })}
//         </div>



//         <hr className="mb-4" />

//         <SubTable
//           title="Line Items"
//           columns={lineItemsColumns}
//           data={formattedDetails.lineItems}
//         />
//         <SubTable
//           title="Taxes Paid (Included in the amount)"
//           columns={transLineTaxesColumns}
//           data={formattedDetails.transLineTaxes}  // Use formatted data for taxes
//         />
//         <SubTable
//           title="Prompts / Information"
//           columns={transactionInfosColumns}
//           data={details.transactionInfos}
//         />


//         <button className="mt-4 themed-button" onClick={onClose}>Close</button>
//       </div>
//     </div>
//   );
// }

// export default TransactionModalAdmin;
